import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isGoBack } from '@/lib/react-router-dom'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { StandardCommission } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getCommissionFilterFields } from 'components/Filter/CommissionFields/commissionFields'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { fetchIntroducingBrokerProfileAccountTypeAction } from 'store/partnerships/partnerships.actions'
import { fetchTradingAccountGroupsAction } from 'store/trading-accounts/trading-accounts.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContextData } from 'utils/TableStateContext'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { StandardCommissionsImportModal } from '../../StandardCommissionsImportModal'
import {
  IntroducingBrokerProfileDetailsForm,
  IntroducingBrokerProfileFormSubmitValues,
} from './IntroducingBrokerProfileDetailsForm'
import { IntroducingBrokerProfileProps, MatchParams } from './types'

const useMapStateToProps = () => ({
  clientTypes: useAppSelector((state) => state.clients.types),
  clientStatus: useAppSelector((state) => state.clients.status),
  complianceVulnerabilityGroups: useAppSelector(
    (state) => state.clients.complianceVulnerabilityGroups
  ),
  users: useAppSelector((state) => state.identity.administrationUsers),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  types: useAppSelector((state) => state.clients.types),
  amlCategories: useAppSelector((state) => state.clients.amlCategories),
  countries: useAppSelector((state) => state.identity.countries),
  introducingBrokerProfileAccountType: useAppSelector(
    (state) => state.partnerships.introducingBrokerProfileAccountType
  ),
  tradingAccountGroup: useAppSelector((state) => state.tradingAccounts.tradingAccountGroup),
})

const useIntroducingBrokerProfileDetailsFilter = (props: IntroducingBrokerProfileProps) => {
  const params = useParams<MatchParams>()
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const [standardCommissionsState, setStandardCommissions] = useState<StandardCommission[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: false })
  const { gridState, gridActions } = useGridState()

  const { tickmillCompanies, introducingBrokerProfileAccountType, tradingAccountGroup } =
    useMapStateToProps()

  useEffect(() => {
    if (isEmpty(introducingBrokerProfileAccountType)) {
      dispatch(fetchIntroducingBrokerProfileAccountTypeAction(partnershipsApiClient))
    }
    if (isEmpty(tradingAccountGroup)) {
      dispatch(fetchTradingAccountGroupsAction(tradingAccountsApiClient))
    }
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.id,
    dispatch,
    introducingBrokerProfileAccountType,
    tradingAccountGroup,
    tickmillCompanies,
  ])

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<StandardCommission[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const query = {
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          }
          const response = await partnershipsApiClient.getProfileStandardCommissions(
            props.profile.id,
            query
          )

          if (response?.items) {
            setStandardCommissions(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<StandardCommission[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        take: gridFilterState.take,
        skip: gridFilterState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleGridPageChange = async (skip: number) => {
    gridStatusActions.setFetching(true)
    const pageIndex = skip / gridState.pageSize + 1

    const searchQuery = getQuery()
    const sortQuery = getSort()

    await gridActions.setGridFilters({
      ...gridState,
      skip,
      pageIndex,
    })

    const response = await partnershipsApiClient.getProfileStandardCommissions(props.profile.id, {
      search: searchQuery,
      sort: sortQuery,
      pageSize: gridState.pageSize,
      skip,
      pageIndex,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    await gridActions.setGridFilters({
      ...gridState,
      skip,
      pageIndex,
      total: response.totalResults,
    })

    if (response?.items) {
      setStandardCommissions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const query = {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    }
    const response = await partnershipsApiClient.getProfileStandardCommissions(
      props.profile.id,
      query
    )

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setStandardCommissions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search ?? getQuery()
      const sortQuery = query?.sort ?? getSort()
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await partnershipsApiClient.getProfileStandardCommissions(props.profile.id, {
        search: searchQuery,
        sort: sortQuery,
        take,
        skip,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setStandardCommissions(response.items)
      }
      gridActions.setGridFilters({
        take: response.pageSize,
        skip: response.pageIndex * (response.pageSize - 1),
        total,
      })
      setTableData({ ...tableData, meta: response })
    },
    // tslint:disable-next-line:max-line-length
    [gridState.filter, gridState.take, gridState.skip] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  return {
    gridStatusState,
    gridStatusActions,
    gridState,
    gridActions,
    tableData,
    setTableData,
    standardCommissionsState,
    handleGridPageChange,
    handleGridSortChange,
    filterFetch,
    getQuery,
    getSort,
  }
}

export const IntroducingBrokerProfileDetailsPage: React.FC<IntroducingBrokerProfileProps> = (
  props
) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const navigate = useNavigate()

  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const [isUploadStandardCommissionsModalOpen, setUploadStandardCommissionsModalOpen] =
    useState<boolean>(false)

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const brokerProfileDetailsFilter = useIntroducingBrokerProfileDetailsFilter(props)
  const { standardCommissionsState } = brokerProfileDetailsFilter
  const { gridStatusState, gridState, tableData } = brokerProfileDetailsFilter
  const { gridActions, setTableData, gridStatusActions } = brokerProfileDetailsFilter
  const { handleGridPageChange, handleGridSortChange, filterFetch, getQuery } =
    brokerProfileDetailsFilter

  const stateToProps = useMapStateToProps()
  const { tickmillCompanies } = stateToProps
  const { introducingBrokerProfileAccountType, tradingAccountGroup } = stateToProps

  const setToast = useContext(ToastContext)

  const handleSubmit = async (values: IntroducingBrokerProfileFormSubmitValues) => {
    const { standardCommissions, ...profile } = values
    try {
      await partnershipsApiClient.updateIntroducingBrokerProfile(profile)
      setToast(successToast('Introducing Broker Profile updated successfully.'))
    } finally {
      props.forceRefresh()
    }
  }

  const handleClone = async () => {
    try {
      await partnershipsApiClient.cloneIntroducingBrokerProfile(props.profile.id)
      setToast(successToast('Introducing Broker Profile cloned successfully.'))
      props.forceRefresh()
      // TODO: move this to links file
      navigate('/brokerage/introducing-broker-profiles')
    } catch (e) {
      console.error(e)
    }
  }

  const handleUploadStandardCommissionsModalOpen = () => {
    setUploadStandardCommissionsModalOpen(true)
  }

  const handleDownloadStandardCommissions = async () => {
    const csvContent =
      await partnershipsApiClient.getIntroducingBrokerProfileByIdUploadStandardCommissions(
        props.profile.id
      )
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' })
    const objUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.setAttribute('href', objUrl)
    a.setAttribute('download', 'standard_commissions.csv')
    a.click()
  }

  const handleStandardCommissionsModalResolve = () => {
    setUploadStandardCommissionsModalOpen(false)
    props.forceRefresh()
  }

  return (
    <Loading isLoading={gridStatusState.isLoading}>
      <React.Fragment>
        {auditLogVisible && (
          <ClientAreaAuditLogModal
            isVisible={auditLogVisible}
            onClose={() => setAuditLogVisible(false)}
            ibProfileId={props.profile.id}
          />
        )}
        {isUploadStandardCommissionsModalOpen && (
          <StandardCommissionsImportModal
            visible
            onResolve={handleStandardCommissionsModalResolve}
            onClose={() => setUploadStandardCommissionsModalOpen(false)}
          />
        )}
        <SelectFiltersFacade
          gridState={gridState}
          gridStatusActions={gridStatusActions}
          gridStatusState={gridStatusState}
          setGridFilters={gridActions.setGridFilters}
          filterFetch={filterFetch}
          filterType={FilterType.IB_PROFILE_COMMISSIONS}
          filterFields={[...getCommissionFilterFields()]}
          getQuery={getQuery}
        />
        <IntroducingBrokerProfileDetailsForm
          introducingBrokerProfile={{
            ...props.profile,
            standardCommissions: standardCommissionsState,
          }}
          permissions={permissions}
          introducingBrokerProfileAccountType={introducingBrokerProfileAccountType}
          tradingAccountGroupType={tradingAccountGroup}
          setAuditLogModal={setAuditLogVisible}
          tickmillCompanies={tickmillCompanies}
          onUploadModalOpen={handleUploadStandardCommissionsModalOpen}
          onDownload={handleDownloadStandardCommissions}
          onSubmit={handleSubmit}
          handleClone={handleClone}
          tableData={tableData}
          onSetTableData={setTableData}
          onHandleGridPageChange={handleGridPageChange}
          onHandleGridSortChange={handleGridSortChange}
        />
      </React.Fragment>
    </Loading>
  )
}
